// Copyright 2025 Greener Power Solutions
// This file is part of the Greener Power Solutions,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import {
  ClientCapability,
  MessageType,
  OperatorMessage,
} from '../../features/assetControl/components/OperatorChat/operatorChat.types';
import { useChatSocketConnection } from './useChatSocketConnection';
import useAuth from 'hooks/useAuth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

interface UseRoomMessagesReturn {
  isJoined: boolean;
  sendMessage: (message: OperatorMessage) => void;
}

/**
 * Hook for managing message transport for a specific chat room
 * Pure transport layer - consuming components handle message storage/processing
 */
export const useDialogue = ({
  unitId,
  capabilities,
  onMessage,
}: {
  unitId: string;
  capabilities: ClientCapability[];
  onMessage?: (message: OperatorMessage) => void;
}): UseRoomMessagesReturn => {
  const { socket, isConnected } = useChatSocketConnection();
  const [isJoined, setIsJoined] = useState(false);
  const capabilitiesMemo = useMemo(() => capabilities, []);
  const { user } = useAuth();
  const { t } = useTranslation();

  // Forward messages to consumer
  const handleMessage = useCallback(
    ({ unit_id, message }: { unit_id: string; message: OperatorMessage }) => {
      if (unit_id === unitId) {
        onMessage?.(message);
      } else {
        throw new Error('Received message for wrong unitId');
      }
    },
    [onMessage, unitId]
  );

  // Send Hello when connected to the room
  const handleJoinConfirmed = useCallback(
    (data: { unit_id: string }) => {
      if (data.unit_id === unitId && socket && isConnected) {
        const helloMessage: OperatorMessage = {
          uuid: uuidv4(),
          message_type: MessageType.HELLO,
          capabilities,
        };
        socket.emit('message', {
          unit_id: unitId,
          message: helloMessage,
        });

        setIsJoined(true);
      }
    },
    [capabilities, socket, isConnected, unitId]
  );

  // Set up message listener
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    socket.on('message', handleMessage);
    socket.on('join_confirmed', handleJoinConfirmed);

    return () => {
      socket.off('message', handleMessage);
      socket.off('join_confirmed', handleJoinConfirmed);
    };
  }, [socket, isConnected, handleMessage, handleJoinConfirmed]);

  // Join the chat room
  useEffect(() => {
    setIsJoined(false);

    if (!socket || !isConnected || !unitId) {
      return;
    }

    socket.emit('join_chat', { rooms: [unitId] });

    return () => {
      // Leave room on cleanup or when unitId changes
      if (socket && isConnected) {
        socket.emit('leave_chat', { rooms: [unitId] });
      }
      setIsJoined(false);
    };
  }, [socket, isConnected, unitId, capabilitiesMemo]);

  const sendMessage = useCallback(
    (message: OperatorMessage) => {
      if (socket && isConnected && unitId && isJoined) {
        const messageWithTimestamp = { ...message, timestamp: new Date().toISOString() };
        const messageToOthers = {
          ...messageWithTimestamp,
          user_info: { first_name: user?.firstName, last_name: user?.lastName },
        };
        const messageToDisplay = {
          ...messageWithTimestamp,
          user_info: { first_name: t('unitControl:chat.you'), last_name: '' },
        };

        socket.emit('message', {
          unit_id: unitId,
          message: messageToOthers,
        });
        onMessage?.(messageToDisplay);
        console.debug(`send message: ${JSON.stringify(messageToOthers)}`);
      }
    },
    [socket, isConnected, isJoined, unitId]
  );

  return {
    isJoined: isJoined && isConnected,
    sendMessage,
  };
};
